import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, forkJoin, Subject } from 'rxjs';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { CanComponentDeactivate } from '../../guards/unsaved-changes.guard';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { FileDescription } from 'components/file-upload/file-upload.component';
import { InfoRequestFormComponent } from 'components/service/info-request/info-request-form/info-request-form.component';
import { InfoRequestClientPayerFormComponent } from 'components/service/info-request/info-request-client-payer-form/info-request-client-payer-form.component';
import { InfoRequestSummaryComponent } from 'components/service/info-request/info-request-summary/info-request-summary.component';
import { ServiceStepsComponent } from 'components/service/service-steps/service-steps.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { combineLatest } from 'rxjs';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { countFileActionFailures, firstFileActionError, isolateFileActions } from 'util/file-upload.utility';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { ClientCard } from 'types/client.interface';
import { LegalEntityCard } from 'types/legal-entity.interface';
import {
  InformationRequestCard,
  InformationRequestClientInfoItem,
  InformationRequestStep,
  InformationRequestTopicResponse
} from 'types/information-request.interface';

@Component({
  selector: 'app-info-request-service-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    InfoRequestFormComponent,
    InfoRequestClientPayerFormComponent,
    InfoRequestSummaryComponent,
    TranslateModule,
    ServiceStepsComponent,
    SpinnerComponent,
    ConfirmModalComponent
  ],
  templateUrl: './info-request-service-page.component.html',
  styleUrl: './info-request-service-page.component.scss'
})
export class InfoRequestServicePageComponent implements OnInit, CanComponentDeactivate {
  isOpenLeaveConfirm: boolean = false;
  private leaveConfirm$?: Subject<boolean>;
  readonly requestStep = InformationRequestStep.Request;
  readonly clientAndPayerStep = InformationRequestStep.ClientAndPayer;
  readonly summaryStep = InformationRequestStep.Summary;

  steps: string[] = [InformationRequestStep.Request, InformationRequestStep.ClientAndPayer, InformationRequestStep.Summary];

  breadcrumbs: Breadcrumb[] = [
    { title: 'components.breadcrumbs.infoRequest', url: '/informacijas-pieprasijums' },
    { title: 'components.breadcrumbs.infoRequestApply', url: '/informacijas-pieprasijums/pievienot' },
    { title: 'components.breadcrumbs.infoRequestForm', url: '' }
  ];

  currentStep: string = InformationRequestStep.Request;
  stepIndex: number = 1;
  highestStepIndex: number = 1;
  client?: ClientCard;
  clientId: string = '';
  clientType: number = 1; // 1=FP, 2=JP
  queryLegalEntityId: string = '';
  application?: InformationRequestCard;
  topics: InformationRequestTopicResponse[] = [];
  isLoading: boolean = true;
  isClientLoading: boolean = false;
  isTopicsLoading: boolean = true;
  isApplicationLoading: boolean = false;
  clientLoadError: boolean = false;
  hasFormChanged: boolean = false;
  /** One-shot: the pending step change came from the discard prompt, so the flush must not run. */
  private discardedStepChange: boolean = false;
  isSubmittingFiles: boolean = false;
  private pendingFileActions: Observable<any>[] = [];
  documentFiles: { files: File[]; descriptions: FileDescription[] } = { files: [], descriptions: [] };
  clientPayerFiles: Record<string, { files: File[]; descriptions: FileDescription[] }> = {};
  /**
   * Queued files a step save has uploaded. They stay in the two maps above (the upload box stops
   * listing a file once the server has it but never says so), so the summary needs this to know which
   * ones "Iesniegt" still has to send (LNA-1850).
   */
  readonly uploadedFiles = new Set<File>();
  legalEntity?: LegalEntityCard;
  jpCompanyData: Record<string, string> = {};
  fpContactData: { email?: string; phone?: string } = {};

  @ViewChild(InfoRequestFormComponent) formComponent?: InfoRequestFormComponent;
  @ViewChild(InfoRequestClientPayerFormComponent) clientPayerFormComponent?: InfoRequestClientPayerFormComponent;

  get isAllLoading(): boolean {
    return this.isClientLoading || this.isTopicsLoading || this.isApplicationLoading;
  }

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);

    combineLatest([this.route.params, this.route.queryParams]).subscribe(([params, queryParams]) => {
      const applicationId = params['id'];
      const newClientId = params['clientId'] || queryParams['clientId'] || '';

      // Reset per-client/request state when navigating to a different client or request
      // without a page rebuild, so one client's data can't leak into another's form (RC-K)
      if ((this.clientId && this.clientId !== newClientId) || (this.application && applicationId && this.application.id !== applicationId)) {
        this.application = undefined;
        this.client = undefined;
        this.legalEntity = undefined;
      }

      this.clientId = newClientId;
      this.clientType = parseInt(queryParams['clientType'] || '1', 10);
      this.queryLegalEntityId = queryParams['legalEntityId'] || '';

      if (applicationId && !this.application) {
        this.isApplicationLoading = true;
        this.apiService.getInformationRequest(applicationId).subscribe({
          next: (data) => {
            this.application = data;
            this.isApplicationLoading = false;
            this.highestStepIndex = Math.max(this.highestStepIndex, this.stepIndex, this.inferStepFromApplication(data));
            if (!this.clientId && data.clientId) {
              this.clientId = data.clientId;
            }
            if (data.legalEntityId) {
              this.clientType = 2;
              if (!this.queryLegalEntityId) {
                this.queryLegalEntityId = data.legalEntityId;
              }
            }
            if (this.clientId) {
              this.loadClientData();
            } else if (this.queryLegalEntityId && !this.clientId) {
              this.loadLegalEntityOnly();
            } else if (data.clientInfo) {
              this.client = this.buildClientFromInfo(data.clientInfo);
              this.isClientLoading = false;
            } else {
              this.isClientLoading = false;
            }
          },
          error: (error) => {
            console.error(error);
            this.isApplicationLoading = false;
          }
        });
      } else if (this.clientId) {
        this.loadClientData();
      }

      if (queryParams['solis']) {
        this.stepIndex = parseInt(queryParams['solis'], 10);
        const newStep = this.getStepByIndex(this.stepIndex);
        // A step change the operator reached through the "izmaiņas netiks saglabātas" prompt must
        // not be saved on the way out; the step components are @if-rendered, so dropping the flush
        // is the whole revert -- the rebuild reads the card again (LNA-1850 R2).
        const wasDiscarded = this.discardedStepChange;
        this.discardedStepChange = false;
        if (wasDiscarded && newStep !== this.currentStep) {
          // The files queued on the step being left are part of what the operator chose not to save.
          // Left in place, the summary listed them as attached and "Iesniegt" sent them (LNA-1850).
          this.dropQueuedFiles(this.currentStep);
        }
        if (this.currentStep === this.requestStep && newStep !== this.requestStep && !wasDiscarded) {
          // The step-1 component is destroyed before the response lands, so its applicationUpdated
          // no longer reaches this page -- adopt the saved card here instead (LNA-1850 R4/R6).
          this.formComponent?.flushStepData().subscribe((data) => {
            if (data) this.handleApplicationUpdated(data);
          });
        }
        this.currentStep = newStep;
        this.highestStepIndex = Math.max(this.highestStepIndex, this.stepIndex);
        this.hasFormChanged = false;
      }

      window.scrollTo(0, 0);
    });

    this.loadTopics();
  }

  private loadClientData(): void {
    this.isClientLoading = true;
    this.clientLoadError = false;
    if (this.clientType === 2) {
      if (this.queryLegalEntityId && this.queryLegalEntityId !== this.clientId) {
        // From search page: have both clientId and legalEntityId
        this.apiService.getClient(this.clientId).subscribe({
          next: (clientCard) => {
            this.client = clientCard;
            this.apiService.getLegalEntity(this.queryLegalEntityId).subscribe({
              next: (le) => {
                this.legalEntity = le;
                this.isClientLoading = false;
              },
              error: () => {
                this.isClientLoading = false;
              }
            });
          },
          error: (error) => {
            console.error(error);
            this.isClientLoading = false;
            this.clientLoadError = true;
          }
        });
      } else {
        // From JP profile page: clientId is actually legalEntityId
        this.apiService.getLegalEntity(this.clientId).subscribe({
          next: (legalEntity) => {
            this.legalEntity = legalEntity;
            this.setFallbackClient(legalEntity);
            this.apiService.getClientByLegalEntityId(this.clientId).subscribe({
              next: (response) => {
                const linked = response.list?.[0];
                if (linked) {
                  this.apiService.getClient(linked.id).subscribe({
                    next: (clientCard) => {
                      this.client = clientCard;
                    }
                  });
                }
              }
            });
          },
          error: (error) => {
            console.error(error);
            this.isClientLoading = false;
            this.clientLoadError = true;
          }
        });
      }
    } else {
      this.apiService.getClient(this.clientId).subscribe({
        next: (data) => {
          this.client = data;
          this.isClientLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.isClientLoading = false;
          this.clientLoadError = true;
        }
      });
    }
  }

  private loadTopics(): void {
    this.isTopicsLoading = true;
    this.apiService.getInformationRequestTopics().subscribe({
      next: (data) => {
        this.topics = data;
        this.isTopicsLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.isTopicsLoading = false;
      }
    });
  }

  getStepByIndex(index: number): string {
    return this.steps[index - 1] || this.steps[0];
  }

  handleApplicationUpdated(application: InformationRequestCard): void {
    // A topic change resets step 2 on the server and deletes its documents (the step-1 ones stay).
    // The summary sends every queued file on "Iesniegt", so step-2 files queued before the change
    // would otherwise be attached to answers that no longer exist (LNA-1850).
    if (this.application && application.topicType !== this.application.topicType) {
      this.dropQueuedFiles(this.clientAndPayerStep);
    }
    this.application = application;
    this.highestStepIndex = Math.max(this.highestStepIndex, this.inferStepFromApplication(application));
  }

  handleFormChanged(hasFormChanged: boolean): void {
    this.hasFormChanged = hasFormChanged;
  }

  handleStepSelected(): void {
    this.hasFormChanged = false;
    // Re-derived from every selection, so a discard cannot leak into the next step change.
    this.discardedStepChange = false;
  }

  handleChangesDiscarded(): void {
    this.discardedStepChange = true;
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.hasFormChanged) return true;
    this.leaveConfirm$ = new Subject<boolean>();
    this.isOpenLeaveConfirm = true;
    return this.leaveConfirm$.asObservable();
  }

  onLeaveConfirm(): void {
    this.isOpenLeaveConfirm = false;
    this.hasFormChanged = false;
    this.leaveConfirm$?.next(true);
    this.leaveConfirm$?.complete();
  }

  onLeaveCancel(): void {
    this.isOpenLeaveConfirm = false;
    this.leaveConfirm$?.next(false);
    this.leaveConfirm$?.complete();
  }

  handleDraftFilesUpload(): void {
    this.pendingFileActions = [];
    const upload = this.formComponent?.documentUpload;
    const deletedIds: string[] = [];
    const sent: File[] = [];

    if (upload && this.application?.id) {
      deletedIds.push(...upload.pendingDeletions);
      sent.push(...upload.files);
      upload.associatedEntityId = this.application.id;
      upload.handleFileRequests();
    }

    this.removeDeletedDocuments(deletedIds);

    this.runPendingFileActions(sent);
  }

  handleClientPayerDraftFilesUpload(): void {
    this.pendingFileActions = [];
    const deletedIds: string[] = [];
    const sent: File[] = [];
    const cpForm = this.clientPayerFormComponent;

    if (cpForm) {
      const uploads = [cpForm.jpAuthorizationUpload, cpForm.authorizationUpload, cpForm.deathCertificateUpload, cpForm.discountGroupUpload];
      for (const upload of uploads) {
        if (upload) {
          deletedIds.push(...upload.pendingDeletions);
          sent.push(...upload.files);
        }
      }
      cpForm.triggerFileUploads();
    }

    this.removeDeletedDocuments(deletedIds);

    this.runPendingFileActions(sent);
  }

  handleFileUploading(fileActions: Observable<any>[]): void {
    this.pendingFileActions.push(...fileActions);
  }

  /**
   * A refused round (the server's per-kind file cap, an expired session) used to be swallowed
   * whole: neither call site passed a `next`/`error` callback, so `forkJoin` cancelled the
   * surviving siblings and the operator was told nothing at all (LNA-1850). Isolate the actions so
   * one refusal cannot take the rest down with it, and report the first failure — `showRequestError`
   * names the cap when that is what the server refused on.
   *
   * `sent` are the queued files this round uploads. Only a round that went through completely marks
   * them as uploaded: after a partial failure the summary sends them again on "Iesniegt", and for the
   * ones that did arrive the server treats that as a no-op (LNA-1587).
   */
  private runPendingFileActions(sent: File[]): void {
    if (this.pendingFileActions.length === 0) return;

    forkJoin(isolateFileActions(this.pendingFileActions)).subscribe((results) => {
      if (countFileActionFailures(results) > 0) {
        this.toastService.showRequestError(firstFileActionError(results));
        return;
      }

      sent.forEach((file) => this.uploadedFiles.add(file));
    });
  }

  handleDocumentsChanged(data: { files: File[]; descriptions: FileDescription[] }): void {
    this.documentFiles = data;
  }

  private removeDeletedDocuments(deletedIds: string[]): void {
    if (deletedIds.length > 0 && this.application) {
      this.application = {
        ...this.application,
        documents: (this.application.documents || []).filter((d) => !deletedIds.includes(d.outerId!))
      };
    }
  }

  handleClientPayerFilesChanged(data: Record<string, { files: File[]; descriptions: FileDescription[] }>): void {
    this.clientPayerFiles = data;
  }

  private dropQueuedFiles(step: string): void {
    if (step === this.requestStep) {
      this.documentFiles = { files: [], descriptions: [] };
    } else if (step === this.clientAndPayerStep) {
      this.clientPayerFiles = {};
    }
  }

  getSelectedTopic(): InformationRequestTopicResponse | undefined {
    if (!this.application?.topicType) return undefined;
    return this.topics.find((t) => t.topicType === this.application!.topicType);
  }

  get lastAvailableStep(): string {
    return this.getStepByIndex(this.highestStepIndex);
  }

  private setFallbackClient(legalEntity: LegalEntityCard): void {
    this.client = {
      id: legalEntity.id,
      clientType: 'Organization',
      fullName: legalEntity.name,
      name: legalEntity.name,
      surname: '',
      nationalDigitalId: legalEntity.registrationNumber,
      officialDocumentId: legalEntity.registrationNumber,
      legalEntityName: legalEntity.name,
      legalEntityId: legalEntity.id,
      country: legalEntity.country,
      authenticationType: legalEntity.authenticationType
    } as ClientCard;
    this.isClientLoading = false;
  }

  private loadLegalEntityOnly(): void {
    this.isClientLoading = true;
    this.apiService.getLegalEntity(this.queryLegalEntityId).subscribe({
      next: (le) => {
        this.legalEntity = le;
        if (this.application?.clientInfo) {
          this.client = this.buildClientFromInfo(this.application.clientInfo);
        }
        this.isClientLoading = false;
      },
      error: () => {
        if (this.application?.clientInfo) {
          this.client = this.buildClientFromInfo(this.application.clientInfo);
        }
        this.isClientLoading = false;
      }
    });
  }

  private inferStepFromApplication(app: InformationRequestCard): number {
    if (app.requesterRelationType || app.payerType || app.serviceDeadline || app.responseDeliveryMethod) {
      return 2;
    }
    return 1;
  }

  private buildClientFromInfo(info: InformationRequestClientInfoItem): ClientCard {
    const isJp = !!info.companyName;
    return {
      id: '',
      authenticationType: 'Local',
      clientType: isJp ? 'Organization' : 'NaturalPerson',
      status: 'Active',
      name: info.name || '',
      surname: info.surname || '',
      fullName: isJp ? info.companyName || '' : `${info.name || ''} ${info.surname || ''}`.trim(),
      legalEntityId: this.queryLegalEntityId || null,
      legalEntityName: info.companyName || null,
      nationalDigitalId: info.personalCode || null,
      officialDocumentId: isJp ? info.registrationNumber || null : info.personalCode || null,
      email: info.email || null,
      phone: info.phone || null,
      createdOn: new Date().toISOString(),
      agreementToPortalTerms: false,
      isIdVerifiedInPerson: false,
      isEmailActivated: false,
      hasReaderCard: false,
      serviceRequestCount: 0,
      clientNoteCount: 0,
      isDeleted: false,
      canBeDeleted: true
    } as ClientCard;
  }
}
